/**
 * Per-category presentation of a fax letter's Authorization section: the status tag and which
 * fields the category shows (Approval, Denial, and NAR have their own layouts; the rest share one).
 * @module faxLetterCategory
 */
import { TagOption } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import {
  FaxLetterAuthorization,
  FaxLetterCategory,
} from "../../../api/postCall/faxLetter";

import { getCategoryTag } from "../../../utils/faxLetterCategoryTags";

export { getCategoryTag } from "../../../utils/faxLetterCategoryTags";
export type { CategoryTag } from "../../../utils/faxLetterCategoryTags";

/** One label/value cell in the Authorization section. */
export interface AuthorizationField {
  key: keyof Omit<FaxLetterAuthorization, "category">;
  label: string;
  /** Spans the full row (long free text). */
  fullWidth?: boolean;
}

const REFERENCE_NUMBER: AuthorizationField = {
  key: "reference_number",
  label: "Reference Number",
};

const APPROVAL_FIELDS: AuthorizationField[] = [
  { key: "reference_number", label: "Auth Number" },
  { key: "start_date", label: "Start Date" },
  { key: "end_date", label: "End Date" },
  { key: "num_visits", label: "Visits" },
  { key: "quantity", label: "Quantity" },
  { key: "notes", label: "Notes", fullWidth: true },
];

const DENIAL_FIELDS: AuthorizationField[] = [
  REFERENCE_NUMBER,
  {
    key: "denial_reason_category",
    label: "Reason for Denial",
  },
  { key: "denial_reason", label: "Specify the Reason" },
];

/** Reviewer-selectable categories; Unclassified is only a placeholder for an unresolved read. */
export const CATEGORY_OPTIONS: TagOption[] = Object.values(FaxLetterCategory)
  .filter((category) => category !== FaxLetterCategory.Unclassified)
  .map((category) => {
    const tag = getCategoryTag(category);
    return {
      label: tag.label,
      value: category,
      backgroundColor: tag.backgroundColor,
      textColor: tag.textColor,
    };
  });

/**
 * Label of the status cell: "Auth" on an approval, "Status" everywhere else.
 *
 * @param category - The letter's category for the J-code
 * @returns The status cell label
 */
export const getStatusLabel = (category: FaxLetterCategory): string =>
  category === FaxLetterCategory.Approval ? "Auth" : "Status";

/**
 * Fields shown after the status cell for a category.
 *
 * @param category - The letter's category for the J-code
 * @returns The ordered field list (NAR and informational categories show a reference number)
 */
export const getAuthorizationFields = (
  category: FaxLetterCategory,
): AuthorizationField[] => {
  switch (category) {
    case FaxLetterCategory.Approval:
      return APPROVAL_FIELDS;
    case FaxLetterCategory.Denial:
      return DENIAL_FIELDS;
    default:
      return [REFERENCE_NUMBER];
  }
};

const UNSAFE_NAME_CHARACTERS = /[^A-Za-z0-9.-]+/g;
const DATE_INPUT_FORMATS = ["YYYY-MM-DD", "MM/DD/YYYY", "MM-DD-YYYY"];

const slug = (value?: string | null): string | null => {
  const token = (value ?? "")
    .trim()
    .replace(UNSAFE_NAME_CHARACTERS, "-")
    .replace(/^-+|-+$/g, "");
  return token || null;
};

const isoDate = (value?: string | null): string | null => {
  const parsed = moment((value ?? "").trim(), DATE_INPUT_FORMATS, true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

export interface DocumentNameParts {
  patientName?: string | null;
  payer?: string | null;
  jCode?: string | null;
  drugName?: string | null;
  category: string;
  dateOfService?: string | null;
  receivedDate?: string | null;
}

/**
 * Suggested document name `{patient}_{payer}_{J-code|drug}_{doc type}_dos-{YYYY-MM-DD}_rcvd-{YYYY-MM-DD}`.
 *
 * Same rules as the categorization layer's composed name: each value is reduced to a filename-safe
 * token, and any field that is missing (or a date that does not parse) is left out.
 *
 * @param parts - The letter's identification, its (possibly corrected) category, and its dates
 * @returns The composed name
 */
export const buildFaxLetterDocumentName = (
  parts: DocumentNameParts,
): string => {
  const segments = [
    slug(parts.patientName),
    slug(parts.payer),
    slug(parts.jCode) ?? slug(parts.drugName),
    parts.category,
  ].filter((segment): segment is string => !!segment);
  const dos = isoDate(parts.dateOfService);
  const received = isoDate(parts.receivedDate);
  if (dos) segments.push(`dos-${dos}`);
  if (received) segments.push(`rcvd-${received}`);
  return segments.join("_");
};
